import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { ticketPriceOn } from '../park-flow/park-catalog';
import { IParkDatePrice } from '../park-calendar/park-calendar.component';

export interface IParkDayOption {
  key: string;     // ticket (options[].key): puede haber varios con el mismo tipo y los mismos días
  value: string;   // cantidad de días como string
  label: string;
  sub: string;
  price: number;   // precio del ticket de ese tipo para esos días
  variant: string; // nombre del ticket cuando hay otro con los mismos días y distinto precio
}

/**
 * Paso de días: los días de parque del ticket elegido (tickets.options[] con type + days, con el precio del ticket) y
 * el calendario de la fecha de inicio. Con el ticket dentro de un paquete (Disney) se puede elegir cualquier día del
 * rango buscado y el precio no cambia con la fecha, solo con la cantidad de días. Con un ticket add-on (Universal)
 * solo se pueden elegir las fechas con disponibilidad (options[].dates), cada una con su precio.
 */
@Component({
  selector: 'usb-park-days',
  templateUrl: './park-days.component.html',
  styleUrls: ['./park-days.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ParkDaysComponent implements OnChanges {
  @Input() options: any[] = [];
  @Input() type: string | null = null;
  @Input() addon = false;
  @Input() adults = 1;
  @Input() children = 0;
  @Input() arrivalDate: string | Date;
  @Input() departureDate: string | Date;
  @Input() selectedDays: string | null = null;
  @Input() selectedKey: string | null = null;
  @Input() selectedDate: string | null = null;
  @Input() sourceCurrency = 'USD';
  @Input() currencies: any[];
  @Input() currencyAgency: any;
  @Input() buttonColorActive = '#6ff3b0';

  @Output() daysChange = new EventEmitter<IParkDayOption>();
  @Output() dateChange = new EventEmitter<string>();

  dayOptions: IParkDayOption[] = [];
  datePrices: IParkDatePrice[] | null = null;

  ngOnChanges(): void {
    const matching = (this.options || []).filter((o: any) => o.type === this.type && Number(o.days) > 0);
    const repeated = (o: any) => matching.filter((x: any) => String(x.days) === String(o.days)).length > 1;
    this.dayOptions = matching
      .map((o: any) => ({
        key: String(o.key || o.ticketKey || o.bookRef || ''),
        value: String(o.days),
        label: String(o.days),
        sub: Number(o.days) === 1 ? 'Día' : 'Días',
        price: Number(o.ticketPriceProf ?? o.ticketPrice ?? 0),
        variant: repeated(o) ? String(o.name || '') : '',
      }))
      .sort((a, b) => Number(a.value) - Number(b.value) || a.price - b.price);

    const option = matching.find((o: any) => String(o.key || o.ticketKey || o.bookRef || '') === this.selectedKey)
      || matching.find((o: any) => String(o.days) === this.selectedDays);
    this.datePrices = this.addon && option
      ? ((option.dates || []) as any[])
          .filter(d => !!d?.date)
          .map(d => {
            const date = String(d.date).substring(0, 10);
            return { date, price: ticketPriceOn(option, date, this.adults, this.children) };
          })
      : null;
  }

  get selectedOption(): IParkDayOption | null {
    return this.dayOptions.find(o => o.key === this.selectedKey)
      || this.dayOptions.find(o => o.value === this.selectedDays)
      || null;
  }

  get selectedCount(): number {
    return Number(this.selectedOption?.value) || 0;
  }

  pick(option: IParkDayOption): void {
    this.selectedDays = option.value;
    this.selectedKey = option.key;
    this.daysChange.emit(option);
  }

  trackByKey(_: number, option: IParkDayOption): string {
    return option.key;
  }
}
